import React, { useState } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WarrantyTimelineChart = () => {
  const [timeRange, setTimeRange] = useState('12months');
  const [showProjections, setShowProjections] = useState(true);

  const warrantyData = [
    { month: 'Aug 2025', expiring: 12, cost: 24000, projected: 15000, critical: 3 },
    { month: 'Sep 2025', expiring: 18, cost: 36000, projected: 22000, critical: 5 },
    { month: 'Oct 2025', expiring: 25, cost: 48000, projected: 31000, critical: 8 },
    { month: 'Nov 2025', expiring: 15, cost: 28000, projected: 18000, critical: 4 },
    { month: 'Dec 2025', expiring: 22, cost: 42000, projected: 26000, critical: 6 },
    { month: 'Jan 2026', expiring: 31, cost: 58000, projected: 35000, critical: 9 },
    { month: 'Feb 2026', expiring: 19, cost: 35000, projected: 21000, critical: 5 },
    { month: 'Mar 2026', expiring: 28, cost: 52000, projected: 32000, critical: 7 },
    { month: 'Apr 2026', expiring: 16, cost: 30000, projected: 19000, critical: 4 },
    { month: 'May 2026', expiring: 24, cost: 45000, projected: 28000, critical: 6 },
    { month: 'Jun 2026', expiring: 33, cost: 62000, projected: 38000, critical: 10 },
    { month: 'Jul 2026', expiring: 21, cost: 38000, projected: 24000, critical: 5 }
  ];

  const upcomingExpirations = [
    {
      id: 'WE-001',
      assetId: 'AT-2025-0234',
      assetName: 'Dell Latitude 7420',
      department: 'Finance',
      expiryDate: '2025-08-25',
      daysLeft: 6,
      replacementCost: 1200,
      priority: 'high'
    },
    {
      id: 'WE-002',
      assetId: 'AT-2025-0156',
      assetName: 'HP EliteBook 840',
      department: 'Marketing',
      expiryDate: '2025-08-28',
      daysLeft: 9,
      replacementCost: 1100,
      priority: 'high'
    },
    {
      id: 'WE-003',
      assetId: 'AT-2025-0089',
      assetName: 'MacBook Pro 16"',
      department: 'IT',
      expiryDate: '2025-09-02',
      daysLeft: 14,
      replacementCost: 2500,
      priority: 'medium'
    },
    {
      id: 'WE-004',
      assetId: 'AT-2025-0312',
      assetName: 'Surface Pro 8',
      department: 'Sales',
      expiryDate: '2025-09-08',
      daysLeft: 20,
      replacementCost: 1300,
      priority: 'medium'
    },
    {
      id: 'WE-005',
      assetId: 'AT-2025-0445',
      assetName: 'ThinkPad X1 Carbon',
      department: 'Operations',
      expiryDate: '2025-09-15',
      daysLeft: 27,
      replacementCost: 1400,
      priority: 'low'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-sm text-muted-foreground">{entry?.name}:</span>
              </div>
              <span className="text-sm font-medium text-popover-foreground">
                {entry?.name?.includes('cost') || entry?.name?.includes('projected')
                  ? `₹${entry?.value?.toLocaleString('en-IN')}`
                  : entry?.value
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return 'AlertTriangle';
      case 'medium':
        return 'Clock';
      case 'low':
        return 'CheckCircle';
      default:
        return 'Info';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Warranty Timeline</h3>
          <p className="text-sm text-muted-foreground">Upcoming expirations and replacement costs</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={showProjections ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowProjections(!showProjections)}
            iconName="TrendingUp"
            iconPosition="left"
          >
            Projections
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="xl:col-span-2">
          <div className="h-80 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={warrantyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="expiringGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC2626" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                />
                <Tooltip content={<CustomTooltip />} />
                
                <Area
                  type="monotone"
                  dataKey="expiring"
                  stroke="#2563EB"
                  fillOpacity={1}
                  fill="url(#expiringGradient)"
                  name="Expiring Assets"
                />
                
                {showProjections && (
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="#059669"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                    name="Projected Cost"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-primary">267</div>
              <div className="text-xs text-muted-foreground">Total Expiring</div>
            </div>
            <div className="text-center p-3 bg-error/5 rounded-lg border border-error/20">
              <div className="text-2xl font-bold text-error">₹487K</div>
              <div className="text-xs text-muted-foreground">Replacement Cost</div>
            </div>
            <div className="text-center p-3 bg-warning/5 rounded-lg border border-warning/20">
              <div className="text-2xl font-bold text-warning">72</div>
              <div className="text-xs text-muted-foreground">Critical Assets</div>
            </div>
          </div>
        </div>

        {/* Upcoming Expirations */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-card-foreground">Upcoming Expirations</h4>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View All
            </Button>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {upcomingExpirations?.map((item) => (
              <div
                key={item?.id}
                className="p-3 border border-border rounded-lg hover:border-primary/50 transition-smooth"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-card-foreground">
                        {item?.assetName}
                      </span>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item?.priority)}`}>
                        <Icon name={getPriorityIcon(item?.priority)} size={12} className="inline mr-1" />
                        {item?.priority?.toUpperCase()}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {item?.assetId} • {item?.department}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="Calendar" size={12} />
                    <span>{item?.daysLeft} days left</span>
                  </div>
                  <div className="font-medium text-card-foreground">
                    ₹{item?.replacementCost?.toLocaleString('en-IN')}
                  </div>
                </div>
                
                <div className="mt-2 bg-muted rounded-full h-1">
                  <div
                    className={`h-1 rounded-full ${
                      item?.daysLeft <= 7 ? 'bg-error' :
                      item?.daysLeft <= 14 ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ width: `${Math.max(10, Math.min(100, (30 - item?.daysLeft) * 3))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyTimelineChart;